import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    FormControlLabel,
    Switch,
    CircularProgress,
    Chip,
    Autocomplete,
} from '@mui/material';
import { useNotify, usePermissions, useTranslate } from 'react-admin';
import { libraryService, LibraryItemType, SaveToLibraryData } from '../../services/libraryService';

interface SaveToLibraryDialogProps {
    open: boolean;
    onClose: () => void;
    contentType: LibraryItemType;
    contentId: number;
    contentTitle: string;
    contentDescription?: string;
    contentTags?: string[];
    onSuccess?: () => void;
}

const SaveToLibraryDialog: React.FC<SaveToLibraryDialogProps> = ({
    open,
    onClose,
    contentType,
    contentId,
    contentTitle,
    contentDescription,
    contentTags,
    onSuccess,
}) => {
    const [title, setTitle] = useState(contentTitle);
    const [description, setDescription] = useState(contentDescription || '');
    const [tags, setTags] = useState<string[]>(contentTags || []);
    const [tagInput, setTagInput] = useState('');
    const [isOperatorContent, setIsOperatorContent] = useState(false);
    const [saving, setSaving] = useState(false);

    const notify = useNotify();
    const translate = useTranslate();
    const { permissions } = usePermissions();
    const isOperator = permissions?.includes('ROLE_OPERATOR');

    // Reset form when dialog opens
    React.useEffect(() => {
        if (open) {
            setTitle(contentTitle);
            setDescription(contentDescription || '');
            setTags(contentTags || []);
            setIsOperatorContent(false);
        }
    }, [open, contentTitle, contentDescription, contentTags]);

    const handleSave = async () => {
        if (!title.trim()) {
            notify(translate('library.saveDialog.titleRequired'), { type: 'error' });
            return;
        }

        setSaving(true);
        try {
            const data: SaveToLibraryData = {
                title: title.trim(),
                description: description.trim() || undefined,
                tags: tags.length > 0 ? tags : undefined,
                isOperatorContent: isOperator ? isOperatorContent : false,
            };

            if (contentType === 'course') {
                await libraryService.saveCourseToLibrary(contentId, data);
            } else {
                await libraryService.saveLessonToLibrary(contentId, data);
            }

            notify(translate('library.saveDialog.success'), { type: 'success' });
            onClose();
            if (onSuccess) {
                onSuccess();
            }
        } catch (err: any) {
            notify(err.response?.data?.error || translate('library.saveDialog.error'), { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleAddTag = () => {
        const newTag = tagInput.trim().toLowerCase();
        if (newTag && !tags.includes(newTag)) {
            setTags([...tags, newTag]);
        }
        setTagInput('');
    };

    const handleDeleteTag = (tagToDelete: string) => {
        setTags(tags.filter(tag => tag !== tagToDelete));
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {translate('library.saveDialog.title')}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    <Chip
                        label={contentType === 'course'
                            ? translate('library.types.course')
                            : translate('library.types.lesson')}
                        color={contentType === 'course' ? 'primary' : 'secondary'}
                        size="small"
                        sx={{ alignSelf: 'flex-start' }}
                    />

                    <TextField
                        label={translate('library.saveDialog.titleLabel')}
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        fullWidth
                        required
                        autoFocus
                    />

                    <TextField
                        label={translate('library.saveDialog.descriptionLabel')}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        fullWidth
                        multiline
                        rows={3}
                    />

                    <Box>
                        <Autocomplete
                            multiple
                            freeSolo
                            options={[]}
                            value={tags}
                            onChange={(_, newValue) => setTags(newValue as string[])}
                            inputValue={tagInput}
                            onInputChange={(_, newInputValue) => setTagInput(newInputValue)}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip
                                        label={option}
                                        {...getTagProps({ index })}
                                        key={option}
                                        size="small"
                                    />
                                ))
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={translate('library.saveDialog.tagsLabel')}
                                    placeholder={translate('library.saveDialog.tagsPlaceholder')}
                                    helperText={translate('library.saveDialog.tagsHelp')}
                                />
                            )}
                        />
                    </Box>

                    {isOperator && (
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={isOperatorContent}
                                    onChange={(e) => setIsOperatorContent(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label={translate('library.saveDialog.operatorContent')}
                        />
                    )}

                    {isOperatorContent && (
                        <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                            <Box sx={{ typography: 'body2', color: 'text.secondary' }}>
                                {translate('library.saveDialog.operatorContentHint')}
                            </Box>
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>
                    {translate('ra.action.cancel')}
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={saving || !title.trim()}
                    startIcon={saving ? <CircularProgress size={16} /> : null}
                >
                    {translate('library.saveDialog.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SaveToLibraryDialog;
